import { useMemo } from 'react';
import { formatCurrency, formatNumber } from './format';
import { computeWaterfallCapacity } from '@/utils/concessionWaterfall';
import type { LendwiseConcession } from '@/services/lendwiseConcessionService';
import type { ProspectLoan } from '../types';

interface ConcessionsStripProps {
  loan: ProspectLoan;
  concessions: LendwiseConcession[];
  cureDollars: number;
  lenderCreditDollars: number;
  /**
   * Concessions & Cure action handler. When omitted, the Manage CTA is not
   * rendered at all (no dead affordance). When a future modal wires this up,
   * pass the open-modal callback.
   */
  onManage?: () => void;
  isViewingSnapshot?: boolean;
}

/**
 * 3-cell summary strip with an optional green Manage CTA. Matches the
 * `.concessions-strip` / `.cs-cell` / `.cs-manage` class system from the
 * design handoff.
 *
 * Concessions "Available" + the progress bar fill are derived from the real
 * waterfall capacity (`computeWaterfallCapacity` over the loan's
 * `rate_lock_buy_side_adjustments` + existing concessions) — legacy parity
 * reference. The strip reflects actual Level 1 / Branch Management / Margin
 * bucket capacity, not the count of active concessions.
 */
export function ConcessionsStrip({
  loan,
  concessions,
  cureDollars,
  lenderCreditDollars,
  onManage,
  isViewingSnapshot = false
}: ConcessionsStripProps) {
  const active = concessions.filter((c) => !c.is_legacy);
  const activeCount = active.length;

  const capacity = useMemo(
    () => computeWaterfallCapacity(loan.rate_lock_buy_side_adjustments, active),
    [loan.rate_lock_buy_side_adjustments, active]
  );

  const originalTotal = capacity.buckets.reduce((sum, b) => sum + b.original, 0);
  const consumedTotal = capacity.buckets.reduce((sum, b) => sum + b.consumed, 0);
  const absorbedPct = originalTotal > 0 ? Math.min(100, (consumedTotal / originalTotal) * 100) : 0;
  const nonEmptyBuckets = capacity.buckets.filter((b) => b.original > 0).length;

  return (
    <section className="concessions-strip" aria-label="Concessions &amp; cure summary">
      <div className="cs-cell">
        <div className="k">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Lender credit
        </div>
        <div className={`v${lenderCreditDollars === 0 ? ' zero' : ''}`}>{formatCurrency(lenderCreditDollars)}</div>
        <div className="sub">{lenderCreditDollars === 0 ? 'of $0 max · not engaged' : 'applied to borrower costs'}</div>
      </div>

      <div className="cs-cell">
        <div className="k">Cost to cure</div>
        <div className={`v${cureDollars === 0 ? ' zero' : ''}`}>{cureDollars === 0 ? '—' : formatCurrency(cureDollars)}</div>
        <div className="sub">{cureDollars === 0 ? 'No open cure items' : 'Variance absorption'}</div>
      </div>

      <div className="cs-cell">
        <div className="k">Concessions · waterfall</div>
        <div className={`v${activeCount === 0 ? ' zero' : ''}`}>
          {activeCount}{' '}
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)' }}>active</span>
        </div>
        <div className="bar" aria-hidden>
          <div
            className="fill"
            style={{
              width: `${absorbedPct}%`,
              background: capacity.isOverAllocated ? 'var(--neg)' : 'var(--pos)'
            }}
          />
        </div>
        <div className="sub">
          Available{' '}
          <b
            className="mono"
            style={{
              color: capacity.totalAvailable > 0 ? 'var(--pos-ink)' : capacity.isOverAllocated ? 'var(--neg-ink)' : 'var(--ink-3)'
            }}
          >
            {formatNumber(capacity.totalAvailable, 3)} pts
          </b>{' '}
          across {nonEmptyBuckets} bucket{nonEmptyBuckets === 1 ? '' : 's'}
        </div>
      </div>

      {onManage && (
        <div className="cs-manage">
          <button type="button" className="btn accent" onClick={onManage} disabled={isViewingSnapshot}>
            <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
            Manage Concessions &amp; Cure
          </button>
        </div>
      )}
    </section>
  );
}
